import HomeContent from "./home.html";
import {Email, IsReadOnly, Logout} from "./auth.js";

import {StartListening, StopListening} from "./main.js";

import {
	EventStreamConnected,
	EventStreamDisconnected,
	EventStreamConnecting,
	EventIDRWaiting,
	EventIDRReceived,
	EventStreamReconnecting,
	EventStreamStale,
} from "./stream-session.js";

import {
	StartListenApps,
	ClearSelectedApp,
	ClearSession,
	SelectedApp,
	SiblingApp,
} from "./apps-list.js";

import {Register as B6Filter} from "./b6-filterfrom.js";

import {SetRenderer, GetRenderer} from "./renderer.js";
import {
	connectedRelay,
	measurements,
	preferredRelay,
	setPreferredRelay,
	startMeasuring,
} from "./relay-choice.js";

import {$, $$, detailOf} from "./dom.js";
import {EventAudioAvailable, EventSoundChanged, setSound, soundOn} from "./audio.js";

export async function HandleHome() {
	const approot = $("#app");
	approot.innerHTML = HomeContent;

	const logoutbtn = $("a#logout");
	const disconnectbtn = $$(".disconnectbtn");

	const renderers = $$("#renderersselection a");
	const rendererBtn = $("#rendererBtn");

	const btnViewOnly = $("#btnViewOnly");

	// hacky solution
	const b6only = $$("[bolt6only]");
	if (!IsReadOnly()) {
		// removeProperty, not `= undefined`: assigning undefined to a CSS property is
		// ignored, so this only ever worked because nothing had set it inline yet.
		b6only.forEach((el) => el.style.removeProperty("display"));
	} else {
		b6only.forEach((el) => (el.style.display = "none"));
	}

	const updateViewOnly = () => {
		if (window.viewonly) {
			btnViewOnly.classList.remove("active");
		} else {
			btnViewOnly.classList.add("active");
		}
	};
	document.addEventListener("ViewOnlyChanged", () => {
		updateViewOnly();
	});

	const scaleMenu = $("#scaleMenu");
	const scaleLabel = $("#scaleLabel");
	if (scaleMenu && scaleLabel) {
		const fmtScale = (v) => `${v}×`;
		const applyScale = (raw, persist) => {
			const v = Number(raw);
			if (!isFinite(v)) return;
			if (persist) window.localStorage.setItem("request-video-scale", String(v));
			scaleLabel.textContent = fmtScale(v);
			$$(".dropdown-item", scaleMenu).forEach((item) => {
				item.classList.toggle("--checked", Number(item.dataset.scale) === v);
			});
			document.dispatchEvent(new CustomEvent("RequestVideoScaleChanged", {detail: {scale: v}}));
		};

		applyScale(window.localStorage.getItem("request-video-scale") || "1.0", false);

		$$(".dropdown-item", scaleMenu).forEach((item) => {
			item.addEventListener("click", () => applyScale(item.dataset.scale, true));
		});
	}

	const statusPill = $("#stream-status");
	const toastStack = $("#toast-stack");

	const STATE_LABELS = {
		idle: "",
		connecting: "Connecting…",
		waiting: "Waiting for source",
		live: "Live",
		reconnecting: "Reconnecting…",
		error: "Disconnected",
		stale: "No signal",
	};

	let _streamState = "idle";

	const setStreamState = (state, detail) => {
		if (!statusPill) return;
		_streamState = state;
		statusPill.dataset.state = state;

		const label = STATE_LABELS[state] || "";
		const labelHtml = `<span class="label">${label}</span>`;
		const retryHtml =
			state === "error"
				? `<button type="button" class="retry" id="stream-retry">Retry</button>`
				: "";
		statusPill.innerHTML = `<span class="dot"></span>${labelHtml}${retryHtml}`;

		if (state === "error") {
			const retry = $("#stream-retry", statusPill);
			if (retry)
				retry.addEventListener("click", () => {
					const last = SelectedApp();
					if (last) {
						document.dispatchEvent(new CustomEvent("ConnectStream", {detail: {workload: last}}));
					}
				});
			showToast({
				title: "Stream disconnected",
				body: (detail && detail.message) || "Lost connection to the source.",
				kind: "err",
			});
		}
	};

	const showToast = ({title, body, kind, ttl = 6000}) => {
		if (!toastStack) return;
		const el = document.createElement("div");
		el.className = "toast" + (kind ? ` --${kind}` : "");
		// textContent, not innerHTML: some of these bodies carry text straight from
		// the server (the bridge reports authorisation failures as a text frame).
		const titleEl = document.createElement("div");
		titleEl.className = "toast-title";
		titleEl.textContent = title;
		const bodyEl = document.createElement("div");
		bodyEl.className = "toast-body";
		bodyEl.textContent = body;
		el.append(titleEl, bodyEl);
		toastStack.appendChild(el);
		setTimeout(() => {
			el.style.transition = "opacity 0.3s ease";
			el.style.opacity = "0";
			setTimeout(() => el.remove(), 300);
		}, ttl);
	};

	document.addEventListener(EventStreamConnecting, () => setStreamState("connecting"));
	document.addEventListener(EventStreamConnected, () => setStreamState("waiting"));
	document.addEventListener(EventIDRWaiting, () => {
		if (_streamState === "live") setStreamState("reconnecting");
		else if (_streamState !== "reconnecting") setStreamState("waiting");
	});
	document.addEventListener(EventIDRReceived, () => setStreamState("live"));
	document.addEventListener(EventStreamDisconnected, (e) =>
		setStreamState("error", e && detailOf(e)),
	);
	document.addEventListener(EventStreamReconnecting, () => setStreamState("reconnecting"));
	// The stream stopped without the socket closing: the app hung, or the path
	// went dead. Say so instead of leaving a frozen frame labelled "Live".
	document.addEventListener(EventStreamStale, () => setStreamState("stale"));

	btnViewOnly.addEventListener("click", () => {
		window.viewonly = !window.viewonly;
		document.dispatchEvent(new Event("ViewOnlyChanged"));
	});

	const offsidebar = $("#offsidebar");
	if (offsidebar) {
		offsidebar.addEventListener("shown.bs.offcanvas", () => {
			const active = $(".app-row.active", offsidebar);
			if (active && active.scrollIntoView) {
				active.scrollIntoView({block: "center"});
			}
		});
	}

	// Shown only for streams that carry audio. Displays what is actually playing, so
	// it reads "off" while the browser's autoplay policy still holds sound back.
	const soundAction = $("#sound-action");
	const soundToggle = $("#sound-toggle");
	const showSound = (on) => {
		const label = on ? "Turn sound off" : "Turn sound on";
		soundToggle.title = label;
		soundToggle.setAttribute("aria-label", label);
		soundToggle.setAttribute("aria-pressed", String(on));
		$(".sound-on", soundToggle).toggleAttribute("hidden", !on);
		$(".sound-off", soundToggle).toggleAttribute("hidden", on);
	};
	soundToggle.addEventListener("click", () => setSound(!soundOn()));
	document.addEventListener(EventSoundChanged, (event) => showSound(detailOf(event).on));
	document.addEventListener(EventAudioAvailable, () => {
		soundAction.hidden = false;
		showSound(soundOn());
	});
	document.addEventListener("b6-session-closed", () => (soundAction.hidden = true));

	$("#fs-toggle").addEventListener("click", () => {
		// iPhone Safari has no element fullscreen (only <video> can), so fake it with CSS.
		if (!document.documentElement.requestFullscreen) {
			document.body.classList.toggle("pseudo-fs");
			return;
		}
		if (!document.fullscreenElement) {
			document.documentElement.requestFullscreen();
		} else {
			document.exitFullscreen();
		}
	});
	// Real fullscreen uses the same layout, so the toggle stays pinned on screen as the way out
	// (Android otherwise only exits via the back gesture, which users don't find).
	document.addEventListener("fullscreenchange", () => {
		document.body.classList.toggle("pseudo-fs", !!document.fullscreenElement);
	});

	// Full-access users take control on every page load; read-only users never can.
	window.viewonly = IsReadOnly();
	updateViewOnly();

	const mapnames = {
		"2d": "2D Canvas",
		webgl: "Web GL",
		webgl2: "Web GL2",
	};

	window.selectedRenderer = GetRenderer();
	const onchangerenderer = (event) => {
		const item = event.target;
		const renderer = item.dataset.renderer;
		rendererBtn.innerHTML = mapnames[renderer];
		window.selectedRenderer = renderer;
		SetRenderer(renderer);
	};

	renderers.forEach((item) => {
		item.addEventListener("click", onchangerenderer);
	});

	// Which server the stream is coming from, and a way to override it.
	//
	// The marked entry is the one this session is ACTUALLY connected to, which is not
	// always the fastest or the preferred one — a probe can fail and demote a
	// candidate. A viewer reporting "it is slow" needs to see where their frames are
	// really coming from, so this reports reality rather than intent.
	const relayMenu = $("#relayMenu");

	const relayLabel = (relay) => {
		try {
			return new URL(relay).hostname.split(".")[0];
		} catch {
			return relay;
		}
	};

	const fastestMeasured = () => {
		const [best] = [...measurements().entries()].sort((a, b) => a[1] - b[1]);
		return best ? best[0] : null;
	};

	const renderRelayMenu = () => {
		if (!relayMenu) return;
		const measured = measurements();
		const pinned = preferredRelay();
		const live = connectedRelay();

		// Nothing to choose between until a relay announces itself, so the section
		// stays hidden rather than showing a single pointless entry.
		if (measured.size < 2) {
			relayMenu.innerHTML = "";
			relayMenu.style.display = "none";
			return;
		}
		relayMenu.style.display = "";

		const entries = [...measured.entries()].sort((a, b) => a[1] - b[1]);
		const items = [
			`<button class="dropdown-item${pinned ? "" : " --checked"}" type="button" data-relay="">Automatic</button>`,
		];
		for (const [relay, ms] of entries) {
			const timing = Number.isFinite(ms) ? `${Math.round(ms)}ms` : "unreachable";
			// The check marks what is live, so with no explicit choice it shows where
			// the stream actually is; with one, it shows whether that choice took
			// effect yet — it only applies to the next connect.
			const checked = pinned ? relay === pinned : relay === live;
			const inUse = relay === live ? " (in use)" : "";
			items.push(
				`<button class="dropdown-item${checked ? " --checked" : ""}" type="button" data-relay="${relay}">` +
					`${relayLabel(relay)} — ${timing}${inUse}</button>`,
			);
		}
		relayMenu.innerHTML = items.join("");
	};

	if (relayMenu) {
		relayMenu.addEventListener("click", (event) => {
			const item = /** @type {HTMLElement} */ (event.target).closest("[data-relay]");
			if (!item) return;
			const chosen = /** @type {HTMLElement} */ (item).dataset.relay;

			if (!chosen) {
				// Automatic is a toggle, not a one-way option. Switching it off pins
				// whatever is carrying the stream right now — which is why it needs no
				// reconnect, the viewer keeps the server they already have and it stops
				// moving underneath them. With no stream yet, it pins what automatic
				// would have chosen, so the toggle always means something.
				setPreferredRelay(preferredRelay() ? null : connectedRelay() || fastestMeasured());
				renderRelayMenu();
				return;
			}

			setPreferredRelay(chosen);
			renderRelayMenu();

			// A deliberate choice of a different server is a request to be moved there,
			// so the stream is restarted rather than left until the next connect. The
			// cost is a reconnect and a key frame, which is what the viewer just asked
			// for; automatic switching still never does this on its own.
			const workload = SelectedApp();
			if (workload && chosen !== connectedRelay()) {
				document.dispatchEvent(new CustomEvent("ConnectStream", {detail: {workload}}));
			}
		});
		// Rebuilt when the menu opens: measurements arrive asynchronously, and the
		// connected relay changes with every stream.
		$("#uactionddbutton")?.addEventListener("click", renderRelayMenu);
		renderRelayMenu();
		// Measured as the page loads and kept current after that, so the menu has real
		// numbers before anything is streamed and the next connect ranks on where the
		// viewer is now rather than where they were when the tab was opened.
		startMeasuring(renderRelayMenu);
	}

	logoutbtn.addEventListener("click", () => {
		// Sign-out tears down the session's own state, not just the credential.
		ClearSession();
		Logout();
	});

	const onConnect = () => {
		$$("[hide-connected]").forEach((el) => (el.style.display = "none"));
		$$("[show-connected]").forEach((el) => (el.style.display = "block"));
	};

	const onDisconnect = () => {
		$$("[hide-connected]").forEach((el) => (el.style.display = "block"));
		$$("[show-connected]").forEach((el) => (el.style.display = "none"));
		ClearSelectedApp();
		setStreamState("idle");
	};

	const shortName = (workload) => {
		const parts = (workload || "").split(":");
		return parts.length > 2 ? parts.slice(2).join(":") : workload;
	};

	const appPrev = $("#app-prev");
	const appNext = $("#app-next");
	const updateAppNav = () => {
		if (appPrev) appPrev.hidden = SiblingApp("prev") === null;
		if (appNext) appNext.hidden = SiblingApp("next") === null;
	};
	const gotoSibling = (dir) => {
		const target = SiblingApp(dir);
		if (target) {
			document.dispatchEvent(new CustomEvent("ConnectStream", {detail: {workload: target}}));
		}
	};
	if (appPrev) appPrev.addEventListener("click", () => gotoSibling("prev"));
	if (appNext) appNext.addEventListener("click", () => gotoSibling("next"));
	document.addEventListener("apps-updated", updateAppNav);

	document.addEventListener("ConnectStream", (event) => {
		const workload = detailOf(event).workload;
		$$("#active-source-name").forEach((el) => {
			el.textContent = shortName(workload);
			el.title = workload;
		});

		//		window.localStorage.setItem("LastWorkload", event.workload);
		onConnect();
		StartListening(detailOf(event).workload, "#canvasroot");
	});

	disconnectbtn.forEach((btn) => {
		btn.addEventListener("click", () => {
			onDisconnect();
			StopListening();
		});
	});

	const update = () => {
		const portrait = document.createElement("b6-viewer");
		portrait.setAttribute("name", Email());
		const userbit = $("#user");
		userbit.innerHTML = "";
		userbit.appendChild(portrait);
		userbit.innerHTML += Email();
	};

	window.addEventListener("login", () => {
		update();
	});

	update();

	StartListenApps();

	B6Filter(approot);
}
